import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
    Banknote,
    CircleMinus,
    Coins,
    DoorClosed,
    DoorOpen,
    History as HistoryIcon,
    Pencil,
    Plug,
    QrCode,
    Trash2,
    Landmark,
    ArrowLeft,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import DialogForm from '../../component/dialog/DialogForm';
import StatusChip from '../../component/common/StatusChip';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import CloseCashDrawer from './components/CloseCashDrawer';
import IntegrationModal from './components/IntegrationModal';
import OpenCashModal from './components/OpenCashModal';
import usePermissions from '../../hooks/usePermissions';
import useIsMobileShell from '../../hooks/useIsMobileShell';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import { MobileCashRegisterDetail } from '../../component/cashregister/MobileCashRegisters';
import SangriaModal from './components/SangriaModal';
import CashRegisterForm from './Form';
import { formatBRMoney, parseBRDateToISO, parseBRMoney, PAYMENT_METHODS, paymentToneColors, summarizeCashSession } from './shared';

// Painel do caixa — DESIGN-SYSTEM.md, página de detalhe standalone. Ref:
// `prototipo-painel-caixa.html`.
//
// Fonte de dados real (sem fabricar nada, ver comentários abaixo em cada
// derivação): `GET /cashregister/{id}` (nome/allows_bleeding),
// `GET /cashregister/{id}/view` (`CodeIntegration`, campo sem tag json em
// `cashFlowViewResponse` — ver `cashregister/viewer.go`),
// `GET /cashregister/{id}/resume` (KPIs/resumo — escopo "hoje", já que não
// existe endpoint "desde a última abertura"; aproximação documentada, igual
// à que o próprio `Resume()` do backend já usa) e `GET /cashregister/{id}/
// history` (sangrias da sessão + marco de abertura/fechamento, usados para
// derivar a janela da sessão atual).
//
// Correção de bug: o caixa não se conecta com nenhuma maquininha (não existe
// essa funcionalidade real) e não há coluna de "local" no schema. A meta
// "Local" foi removida e a ação/meta "Integração" agora só expõe o dado real
// (`CodeIntegration`), sem simular seleção de dispositivo/sincronização
// (ver `components/IntegrationModal.js`).
function KpiCard({ icon: Icon, tone, value, label, note }) {
    return (
        <Box sx={{ minWidth: 0, p: { xs: '10px', md: '16px' }, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: (t) => `${t.shape.borderRadius}px`, boxShadow: (t) => t.customShadows?.sm }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: { xs: '6px', md: '12px' } }}>
                <Box sx={{ width: { xs: 24, md: 34 }, height: { xs: 24, md: 34 }, borderRadius: { xs: '7px', md: '9px' }, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${tone}.bg`, color: `${tone}.main` }}>
                    <Icon size={16} strokeWidth={2} />
                </Box>
            </Box>
            <Typography sx={{ fontSize: { xs: '17px', md: '21px' }, fontWeight: 800, letterSpacing: '-.02em', mb: '2px', fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{value}</Typography>
            <Typography sx={{ fontSize: { xs: '11px', md: '11.5px' }, color: 'text.secondary', lineHeight: 1.25 }}>{label}{note ? ` ${note}` : ''}</Typography>
        </Box>
    );
}

function ActionButton({ icon: Icon, label, onClick, disabled, title, danger }) {
    const button = (
        <Box
            component="button"
            onClick={disabled ? undefined : onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                padding: '9px 14px',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,.3)',
                background: 'rgba(255,255,255,.12)',
                color: '#fff',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.45 : 1,
                fontFamily: 'inherit',
                '&:hover': disabled ? {} : { background: danger ? '#fecaca' : 'rgba(255,255,255,.22)', color: danger ? '#7f1d1d' : '#fff' },
            }}
        >
            <Icon size={14} strokeWidth={2} />
            {label}
        </Box>
    );
    return title ? <Tooltip title={title}><span>{button}</span></Tooltip> : button;
}

export default function CashRegisterView() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const { id } = useParams();
    const location = useLocation();
    const isMobileShell = useIsMobileShell();

    const [loading, setLoading] = useState(true);
    const [cash, setCash] = useState(null); // { name, allows_bleeding, default }
    const [codeIntegration, setCodeIntegration] = useState('');
    const [resume, setResume] = useState(null);
    const [history, setHistory] = useState([]);
    const [overrides, setOverrides] = useState({}); // local-only: "Caixa ativo" (ver Form.js/List.js)
    const [justClosed, setJustClosed] = useState(null); // { totalCounted, diff } — só existe logo após um fechamento nesta sessão do navegador

    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [openModalOpen, setOpenModalOpen] = useState(false);
    const [sangriaModalOpen, setSangriaModalOpen] = useState(false);
    const [integrationModalOpen, setIntegrationModalOpen] = useState(false);
    const [closeDrawerOpen, setCloseDrawerOpen] = useState(false);

    const showError = (responseData) => {
        let text = 'Não foi possível concluir a operação.';
        if (responseData == null || responseData === '') {
            text = 'Não foi possível concluir a operação.';
        } else if (typeof responseData === 'object') {
            text = responseData.message || responseData.error || JSON.stringify(responseData);
        } else {
            text = String(responseData);
        }
        setSeverity('error');
        setMessage(text);
        setOpenSnackbar(true);
    };
    const showSuccess = (text) => {
        setSeverity('success');
        setMessage(text);
        setOpenSnackbar(true);
    };

    useAppBarTitle(isMobileShell && cash ? cash.name : '');

    // Shell mobile: as telas de abrir/fechar/sangria (página cheia) devolvem o aviso e o resultado do fechamento.
    useEffect(() => {
        const state = location.state;
        if (!state?.toast) return;
        showSuccess(state.toast);
        if (state.closed) setJustClosed(state.closed);
        navigate(location.pathname, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [dataRes, viewRes, resumeRes, historyRes] = await Promise.all([
                httpClient.get(`/cashregister/${id}`),
                httpClient.get(`/cashregister/${id}/view`),
                httpClient.get(`/cashregister/${id}/resume`),
                httpClient.get(`/cashregister/${id}/history`).catch(() => ({ data: [] })),
            ]);
            setCash(dataRes.data);
            setCodeIntegration(viewRes.data?.CodeIntegration || '');
            setResume(resumeRes.data);
            setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
        } catch (error) {
            console.error('Erro ao carregar painel do caixa:', error);
            showError('Não foi possível carregar os dados deste caixa.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const {
        isOpen, sangriasList, totalSangrias, bruto, liquido, pix, machineGross, vendasDinheiro, valorInicial, valorEsperado,
    } = useMemo(() => summarizeCashSession(resume, history), [resume, history]);

    const cashRegisterForModals = cash ? { id, name: cash.name } : null;

    const handleDelete = async () => {
        setDeleting(true);
        try {
            // `httpClient` nunca rejeita por status HTTP — status checado à
            // mão (ver nota equivalente em `List.js`).
            const response = await httpClient.delete(`/cashregister/${id}`);
            if (response.status === 200 || response.status === 204) {
                navigate('/cashregisters');
            } else {
                showError(response.data || 'Não foi possível excluir o caixa.');
            }
        } catch (error) {
            console.error('Erro ao excluir caixa:', error);
            showError(error.response?.data || error.message || 'Não foi possível excluir o caixa.');
        } finally {
            setDeleting(false);
        }
    };

    // Shell mobile: detalhe do protótipo `caixas/detalhe.html` (mesmos dados, permissões e ações).
    if (isMobileShell) {
        return (
            <PageScaffold links={[]} allowBleed titlePageTarget="Painel do caixa" message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
                <MobileCashRegisterDetail
                    loading={loading}
                    cash={cash}
                    isOpen={isOpen}
                    operator={resume?.details?.operator || ''}
                    openDate={resume?.details?.open_date || ''}
                    balance={valorEsperado}
                    initialAmount={valorInicial}
                    summary={{ vendasDinheiro, pix, machineGross, totalSangrias, sangriasList }}
                    justClosed={!isOpen ? justClosed : null}
                    canOpen={hasPermission('cashregister.open')}
                    canClose={hasPermission('cashregister.close')}
                    canBleed={hasPermission('cashregister.bleed')}
                    canHistory={hasPermission('cashregister.history')}
                    canUpdate={hasPermission('cashregister.update')}
                    canDelete={hasPermission('cashregister.delete')}
                    onOpenCash={() => navigate(`/cashregisters/open/${id}`)}
                    onCloseCash={() => navigate(`/cashregisters/close/${id}`)}
                    onSangria={() => navigate(`/cashregisters/sangria/${id}`)}
                    onHistory={() => navigate(`/cashregisters/history/${id}`)}
                    onEdit={() => navigate(`/cashregisters/edit/${id}`)}
                    onIntegration={() => setIntegrationModalOpen(true)}
                    onDelete={() => setDeleteOpen(true)}
                />
                {cash && (
                    <>
                        <DialogForm
                            open={deleteOpen}
                            iconType="warning"
                            onClose={() => setDeleteOpen(false)}
                            onSubmit={handleDelete}
                            title="Excluir Caixa"
                            content={`Tem certeza que deseja excluir o caixa "${cash.name}"?`}
                            secondaryContent="Esta ação não pode ser desfeita."
                            confirmButtonText="Confirmar Exclusão"
                            cancelButtonText="Cancelar"
                            loading={deleting}
                            loadingText="Excluindo..."
                        />
                        <IntegrationModal
                            open={integrationModalOpen}
                            cashRegister={{ id, name: cash.name }}
                            onClose={() => setIntegrationModalOpen(false)}
                        />
                    </>
                )}
            </PageScaffold>
        );
    }

    if (loading || !cash) {
        return (
            <PageScaffold links={[]} titlePageTarget="Painel do caixa">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress size={40} />
                </Box>
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget="Painel do caixa" message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
            <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
                {/* cash-header */}
                <Box sx={{
                    borderRadius: '20px',
                    p: '22px 24px',
                    mb: '18px',
                    color: '#fff',
                    background: (t) => t.palette.gradient.brand,
                    boxShadow: (t) => t.customShadows?.md,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: '18px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <Box
                                component="button"
                                onClick={() => navigate('/cashregisters')}
                                sx={{ width: 36, height: 36, borderRadius: '10px', background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.25)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { background: 'rgba(255,255,255,.25)' } }}
                                title="Voltar para a lista de caixas"
                            >
                                <ArrowLeft size={16} strokeWidth={2} />
                            </Box>
                            <Typography sx={{ fontSize: '21px', fontWeight: 800, letterSpacing: '-.01em' }}>{cash.name}</Typography>
                            <StatusChip domain="cashRegisterStatus" value={isOpen ? 'open' : 'closed'} sx={{ bgcolor: 'rgba(255,255,255,.22)', color: '#fff', '&::before': { backgroundColor: '#fff' } }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {isOpen ? (
                                hasPermission('cashregister.close') && <ActionButton icon={DoorClosed} label="Fechar caixa" onClick={() => setCloseDrawerOpen(true)} />
                            ) : (
                                hasPermission('cashregister.open') && <ActionButton icon={DoorOpen} label="Abrir caixa" onClick={() => setOpenModalOpen(true)} />
                            )}
                            {hasPermission('cashregister.bleed') && (
                                <ActionButton icon={CircleMinus} label="Sangria" onClick={() => setSangriaModalOpen(true)} disabled={!isOpen} title={!isOpen ? 'Abra o caixa para registrar uma sangria' : ''} />
                            )}
                            {hasPermission('cashregister.history') && (
                                <ActionButton icon={HistoryIcon} label="Histórico" onClick={() => navigate(`/cashregisters/history/${id}`)} />
                            )}
                            <ActionButton icon={Plug} label="Integração" onClick={() => setIntegrationModalOpen(true)} />
                            {hasPermission('cashregister.update') && (
                                <ActionButton icon={Pencil} label="Editar" onClick={() => setEditOpen(true)} />
                            )}
                            {hasPermission('cashregister.delete') && (
                                <ActionButton icon={Trash2} label="Excluir" onClick={() => setDeleteOpen(true)} disabled={isOpen} title={isOpen ? 'Feche o caixa antes de excluir' : ''} danger />
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: '18px' }}>
                        <Box>
                            <Typography sx={{ fontSize: 10.5, opacity: .8, textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px' }}>{isOpen ? 'Operador atual' : 'Fechado por'}</Typography>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{resume?.details?.operator || '—'}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 10.5, opacity: .8, textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px' }}>{isOpen ? 'Aberto desde' : 'Fechado desde'}</Typography>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{resume?.details?.open_date || '—'}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 10.5, opacity: .8, textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px' }}>Integração</Typography>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                                {codeIntegration || '—'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* KPIs */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, 1fr)' }, gap: { xs: '8px', md: '14px' }, mb: { xs: '10px', md: '18px' } }}>
                    <KpiCard icon={Banknote} tone="info" value={`R$ ${formatBRMoney(bruto)}`} label="Total bruto" />
                    <KpiCard icon={Banknote} tone="success" value={`R$ ${formatBRMoney(liquido)}`} label="Total líquido" />
                    <KpiCard icon={QrCode} tone="brand2" value={`R$ ${formatBRMoney(pix)}`} label="Total em PIX" />
                    <KpiCard icon={Coins} tone="success" value={`R$ ${formatBRMoney(valorEsperado)}`} label="Troco disponível (dinheiro em caixa)" />
                    <Tooltip title="Não disponível — o backend não registra gorjetas/caixinha hoje.">
                        <span>
                            <KpiCard icon={Landmark} tone="warning" value="—" label="Caixinha (gorjetas)" />
                        </span>
                    </Tooltip>
                </Box>

                {/* Resumo + Formas de pagamento */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '16px', mb: '18px' }}>
                    <Box sx={{ p: '18px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: (t) => `${t.shape.borderRadius}px`, boxShadow: (t) => t.customShadows?.sm }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: '14px' }}>Resumo do caixa</Typography>
                        <Box sx={{ bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: '16px' }}>
                            <SummaryRow label="Valor inicial (abertura)" value={`R$ ${formatBRMoney(valorInicial)}`} />
                            <SummaryRow label="+ Vendas em dinheiro" value={`+ R$ ${formatBRMoney(vendasDinheiro)}`} color="success.main" />
                            <SummaryRow label="− Sangrias" value={`− R$ ${formatBRMoney(totalSangrias)}`} color="error.main" />
                            <SummaryRow total label={isOpen ? 'Valor esperado em caixa' : 'Valor esperado no fechamento'} value={`R$ ${formatBRMoney(valorEsperado)}`} color="success.main" />
                            {!isOpen && justClosed && (
                                <>
                                    <SummaryRow label="Valor apurado no fechamento" value={`R$ ${formatBRMoney(justClosed.totalCounted)}`} />
                                    <SummaryRow
                                        total
                                        label="Diferença de caixa"
                                        value={Math.abs(justClosed.diff) < 0.005 ? 'R$ 0,00 (conferido)' : `${justClosed.diff > 0 ? '+ ' : '− '}R$ ${formatBRMoney(Math.abs(justClosed.diff))}`}
                                        color={Math.abs(justClosed.diff) < 0.005 ? 'success.main' : (justClosed.diff > 0 ? 'success.main' : 'error.main')}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ p: '18px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: (t) => `${t.shape.borderRadius}px`, boxShadow: (t) => t.customShadows?.sm }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: '14px' }}>Formas de pagamento</Typography>
                        {PAYMENT_METHODS.map((m) => {
                            const { bg, fg } = paymentToneColors(theme, m.tone);
                            const Icon = m.icon;
                            const val = m.key === 'dinheiro' ? vendasDinheiro : m.key === 'pix' ? pix : machineGross;
                            return (
                                <Box key={m.key} sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '13px', bgcolor: 'background.subtle', borderRadius: '12px', mb: '8px', '&:last-child': { mb: 0 } }}>
                                    <Box sx={{ width: 34, height: 34, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: bg, color: fg }}>
                                        <Icon size={16} strokeWidth={2} />
                                    </Box>
                                    <Typography sx={{ fontSize: 13 }}>{m.label}</Typography>
                                    <Typography sx={{ ml: 'auto', fontWeight: 800, fontSize: '13.5px' }}>R$ {formatBRMoney(val)}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Sangrias registradas */}
                <Box sx={{ p: '18px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: (t) => `${t.shape.borderRadius}px`, boxShadow: (t) => t.customShadows?.sm }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mb: '14px' }}>Sangrias registradas</Typography>
                    {sangriasList.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 1, color: 'text.disabled', textAlign: 'center' }}>
                            <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CircleMinus size={22} strokeWidth={1.6} />
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: 'text.primary' }}>Nenhuma sangria registrada</Typography>
                            <Typography sx={{ fontSize: 12 }}>Sangrias feitas durante esta sessão aparecerão aqui.</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <Box component="thead">
                                    <Box component="tr">
                                        {['Data/hora', 'Operador', 'Motivo', 'Valor'].map((h, i) => (
                                            <Box component="th" key={h} sx={{ textAlign: i === 3 ? 'right' : 'left', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', pb: '10px', borderBottom: '1px solid', borderColor: 'divider' }}>{h}</Box>
                                        ))}
                                    </Box>
                                </Box>
                                <Box component="tbody">
                                    {sangriasList.map((s, i) => (
                                        <Box component="tr" key={i}>
                                            <Box component="td" sx={{ py: '12px', borderBottom: '1px solid', borderColor: 'divider', fontWeight: 700 }}>{s.DateOperationFormatted}</Box>
                                            <Box component="td" sx={{ py: '12px', borderBottom: '1px solid', borderColor: 'divider' }}>{s.User}</Box>
                                            <Box component="td" sx={{ py: '12px', borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>
                                                {s.ReasonBleending || <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}
                                            </Box>
                                            <Box component="td" sx={{ py: '12px', borderBottom: '1px solid', borderColor: 'divider', textAlign: 'right', fontWeight: 800, color: 'error.main' }}>− R$ {s.Amount}</Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* drawer: editar */}
            <Drawer anchor="right" open={editOpen} onClose={() => setEditOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } }}>
                {editOpen && (
                    <CashRegisterForm
                        embedded
                        cashRegisterId={id}
                        initialOverrides={overrides}
                        onClose={() => setEditOpen(false)}
                        onSuccess={(updated) => {
                            setEditOpen(false);
                            const { active } = updated || {};
                            setOverrides((prev) => ({ ...prev, active }));
                            fetchAll();
                            showSuccess('Caixa atualizado com sucesso!');
                        }}
                        onError={showError}
                    />
                )}
            </Drawer>

            {/* modal: excluir */}
            <DialogForm
                open={deleteOpen}
                iconType="warning"
                onClose={() => setDeleteOpen(false)}
                onSubmit={handleDelete}
                title="Excluir Caixa"
                content={`Tem certeza que deseja excluir o caixa "${cash.name}"?`}
                secondaryContent="Esta ação não pode ser desfeita."
                confirmButtonText="Confirmar Exclusão"
                cancelButtonText="Cancelar"
                loading={deleting}
                loadingText="Excluindo..."
            />

            {/* modal: abrir caixa */}
            <OpenCashModal
                open={openModalOpen}
                cashRegister={cashRegisterForModals}
                onClose={() => setOpenModalOpen(false)}
                onError={showError}
                onSuccess={() => {
                    setOpenModalOpen(false);
                    setJustClosed(null);
                    showSuccess(`Caixa "${cash.name}" aberto com sucesso.`);
                    fetchAll();
                }}
            />

            {/* modal: sangria */}
            <SangriaModal
                open={sangriaModalOpen}
                cashRegister={cashRegisterForModals}
                onClose={() => setSangriaModalOpen(false)}
                onError={showError}
                onSuccess={({ amount }) => {
                    setSangriaModalOpen(false);
                    showSuccess(`Sangria de R$ ${formatBRMoney(parseBRMoney(amount))} registrada em "${cash.name}".`);
                    fetchAll();
                }}
            />

            {/* modal: integração */}
            <IntegrationModal
                open={integrationModalOpen}
                cashRegister={{ id, name: cash.name }}
                onClose={() => setIntegrationModalOpen(false)}
            />

            {/* drawer: fechar caixa */}
            <CloseCashDrawer
                open={closeDrawerOpen}
                cashRegister={cashRegisterForModals}
                sessionStart={{ label: resume?.details?.open_date || '—', dateIso: parseBRDateToISO(resume?.details?.open_date) }}
                expected={{ dinheiro: valorEsperado, pix, cartao: machineGross }}
                onClose={() => setCloseDrawerOpen(false)}
                onError={showError}
                onSuccess={({ totalCounted, diff }) => {
                    setCloseDrawerOpen(false);
                    setJustClosed({ totalCounted, diff });
                    showSuccess(`Caixa "${cash.name}" fechado com sucesso.`);
                    fetchAll();
                }}
            />
        </PageScaffold>
    );
}

function SummaryRow({ label, value, color, total }) {
    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: total ? 0 : '7px',
            pt: total ? '14px' : '7px',
            mt: total ? '6px' : 0,
            borderTop: total ? '1px solid' : 'none',
            borderColor: 'divider',
        }}>
            <Typography sx={{ fontSize: total ? 16 : 13, fontWeight: total ? 800 : 400, color: total ? 'text.primary' : 'text.secondary' }}>{label}</Typography>
            <Typography sx={{ fontSize: total ? 16 : 13, fontWeight: 700, color: color || 'text.primary' }}>{value}</Typography>
        </Box>
    );
}
